/**
 * Toast Utility Functions
 * Helper functions for the custom toast notification system
 */

/**
 * Generate a unique toast ID using timestamp and random string
 * @returns {string} Unique toast identifier
 */
export const generateToastId = () => {
  return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get default duration based on toast type
 * @param {string} type - Toast type (success, error, warning, info, loading)
 * @returns {number} Duration in milliseconds
 */
export const getDefaultDuration = (type) => {
  const durations = {
    success: 3000, // 3 seconds for success messages
    error: 5000, // 5 seconds for error messages (more time to read)
    warning: 4000, // 4 seconds for warnings
    info: 4000, // 4 seconds for info messages
    loading: 0, // No auto-dismiss for loading toasts
  };
  return durations[type] || 4000;
};

/**
 * Get Tailwind color classes based on toast type
 * Returns border, icon, and background color classes for each toast type
 * @param {string} type - Toast type (success, error, warning, info, loading)
 * @returns {object} Object containing border, icon, and bg color classes
 */
export const getToastColors = (type) => {
  const colors = {
    success: {
      border: "border-l-[#f6a332]",
      icon: "text-[#f6a332]",
      bg: "bg-white",
    },
    error: {
      border: "border-l-[#E31837]",
      icon: "text-[#E31837]",
      bg: "bg-white",
    },
    warning: {
      border: "border-l-[#f6a332]",
      icon: "text-[#f6a332]",
      bg: "bg-white",
    },
    info: {
      border: "border-l-[#505A64]",
      icon: "text-[#505A64]",
      bg: "bg-white",
    },
    loading: {
      border: "border-l-[#f6a332]",
      icon: "text-[#f6a332]",
      bg: "bg-white",
    },
  };
  return colors[type] || colors.info;
};

/**
 * Parse error response and return user-friendly error message
 * @param {Error} error - The error object from API call
 * @param {string} defaultMessage - Default message if error cannot be parsed
 * @param {string} action - The action being performed (e.g., 'follow', 'unfollow')
 * @returns {string} User-friendly error message
 */
export const parseErrorMessage = (error, defaultMessage, action = "action") => {
  // Handle specific error types
  if (error.message === "SELF_FOLLOW_NOT_ALLOWED") {
    return "You cannot follow yourself.";
  }

  // Handle network errors
  if (error.code === "ECONNABORTED") {
    return "Request timed out. Please check your connection and try again.";
  }

  if (error.request && !error.response) {
    return "Network error. Please check your connection and try again.";
  }

  // Handle HTTP response errors
  if (error.response) {
    const status = error.response.status;
    const serverMessage = error.response?.data?.message;

    switch (status) {
      case 400:
        if (serverMessage?.includes("already following")) {
          return "You are already following this user.";
        }
        if (serverMessage?.includes("not following")) {
          return "You are not following this user.";
        }
        return serverMessage || `Invalid ${action} request.`;

      case 401:
        return "Authentication error. Please refresh the page and try again.";

      case 403:
        return "You don't have permission to perform this action.";

      case 404:
        if (action === "follow" || action === "unfollow") {
          return "User not found.";
        }
        return "The requested resource was not found.";

      case 409:
        return "You are already following this user.";

      case 429:
        return "Too many requests. Please wait a moment and try again.";

      case 500:
      case 502:
      case 503:
      case 504:
        return "Server error. Please try again in a moment.";

      default:
        return serverMessage || defaultMessage;
    }
  }

  return defaultMessage;
};

/**
 * Determine if an error should be retried
 * @param {Error} error - The error object
 * @param {number} failureCount - Number of previous failures
 * @param {number} maxRetries - Maximum number of retries allowed
 * @returns {boolean} Whether the error should be retried
 */
export const shouldRetryError = (error, failureCount, maxRetries = 2) => {
  if (failureCount >= maxRetries) {
    return false;
  }

  // Don't retry validation errors or client errors
  if (error.message === "SELF_FOLLOW_NOT_ALLOWED") {
    return false;
  }

  if (error.response) {
    const status = error.response.status;
    // Don't retry 4xx errors (client errors) except for 408 (timeout) and 429 (rate limit)
    if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
      return false;
    }
  }

  return true;
};

/**
 * Calculate retry delay with exponential backoff and jitter
 * @param {number} attemptIndex - The attempt number (0-based)
 * @param {number} baseDelay - Base delay in milliseconds
 * @param {number} maxDelay - Maximum delay in milliseconds
 * @returns {number} Delay in milliseconds
 */
export const calculateRetryDelay = (
  attemptIndex,
  baseDelay = 1000,
  maxDelay = 5000
) => {
  // Exponential backoff: baseDelay * 2^attemptIndex
  const exponentialDelay = baseDelay * Math.pow(2, attemptIndex);

  // Add jitter (random factor between 0.5 and 1.5)
  const jitter = 0.5 + Math.random();
  const delayWithJitter = exponentialDelay * jitter;

  // Cap at maximum delay
  return Math.min(delayWithJitter, maxDelay);
};
